import { UserTeamDrawer, ReportsToNote, teamSyncNotice } from './UserTeamDrawer';
import { useState, useEffect, useMemo } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { api } from '@/utils/api';
import { useAuthStore } from '@/stores/auth';
import { PipelinesTab } from './PipelinesTab';
import { Modal, useModalClose } from '@/components/ui/Modal';
import { LeadAutomationTab } from './LeadAutomationTab';
import { SalesTeamsTab } from './SalesTeamsTab';
import { GSTComplianceTab } from './GSTComplianceTab';
import { ModulesExplorer } from './ModulesExplorer';
import { ChatKartBridgeCard } from './ChatKartBridgeCard';
import { PositionsSection } from './PositionsSection';
import { SimRegistryTab } from './SimRegistryTab';
import { KnowledgeAccessSection } from '../../components/kiaan/knowledge/RolesKnowledgeAccess';
import {
  Settings, Users, Shield, Box, Plug, Building2, Loader2, X, Plus, Save, Trash2,
  Pencil, ToggleLeft, ToggleRight, GitBranch, Zap, Crown, ChevronDown, ChevronUp,
  UserCheck, Percent, Factory, FileSignature, LayoutGrid, Smartphone,
} from 'lucide-react';

const ALL_TABS = [
  { id: 'general', label: 'General', icon: Settings, adminOnly: false },
  { id: 'company', label: 'Company Profile', icon: Building2, adminOnly: false },
  { id: 'users', label: 'Users & Roles', icon: Users, adminOnly: false },
  { id: 'departments', label: 'Departments & Positions', icon: Building2, adminOnly: false },
  { id: 'modules', label: 'Modules', icon: Box, adminOnly: false },
  { id: 'modules-explorer', label: 'Modules Explorer', icon: LayoutGrid, adminOnly: false },
  { id: 'pipelines', label: 'Pipelines', icon: GitBranch, adminOnly: false },
  { id: 'lead-automation', label: 'Lead Automation', icon: Zap, adminOnly: false },
  { id: 'sales-teams', label: 'Sales Teams', icon: UserCheck, adminOnly: false },
  { id: 'discount-policy', label: 'Discount Policy', icon: Percent, adminOnly: false },
  { id: 'manufacturing', label: 'Manufacturing', icon: Factory, adminOnly: false },
  { id: 'gst-compliance', label: 'GST Compliance', icon: FileSignature, adminOnly: false },
  { id: 'sim-registry', label: 'SIM Registry', icon: Smartphone, adminOnly: true },
  { id: 'ecosystem', label: 'Integrations', icon: Plug, adminOnly: true },
  { id: 'tenants', label: 'Tenants', icon: Shield, adminOnly: true },
];

const ROLES = ['admin', 'manager', 'sales_rep', 'production_manager', 'service_tech', 'accountant', 'viewer'];

// ==================== Company Profile Tab ====================
function CompanyProfileTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({
    name: '', legalName: '', brandName: '', displayNameMode: 'brand' as string,
    gstin: '', pan: '', stateCode: '',
    line1: '', city: '', state: '', pincode: '',
    phone: '', email: '', website: '',
  });
  const [gstError, setGstError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/settings/company-profile');
        const p = res.data.data;
        if (p) {
          const addr = (p.address && typeof p.address === 'object') ? p.address as Record<string, string> : {};
          setForm({
            name: p.name || '', legalName: p.legalName || '', brandName: p.brandName || '',
            displayNameMode: p.displayNameMode || 'brand',
            gstin: p.gstin || '', pan: p.pan || '', stateCode: p.stateCode || '',
            line1: addr.line1 || '', city: addr.city || '', state: addr.state || '', pincode: addr.pincode || '',
            phone: p.phone || '', email: p.email || '', website: p.website || '',
          });
        }
      } catch {}
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.put('/settings/company-profile', {
        name: form.name, legalName: form.legalName, brandName: form.brandName,
        displayNameMode: form.displayNameMode,
        gstin: form.gstin, pan: form.pan, stateCode: form.stateCode,
        address: { line1: form.line1, city: form.city, state: form.state, pincode: form.pincode },
        phone: form.phone, email: form.email, website: form.website,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleGstinBlur = async () => {
    if (!form.gstin || form.gstin.length < 15) { setGstError(''); return; }
    try {
      const res = await api.post('/settings/company-profile/validate-gstin', { gstin: form.gstin });
      const d = res.data.data;
      setGstError('');
      setForm(prev => ({
        ...prev,
        pan: d.pan || prev.pan,
        stateCode: d.stateCode || prev.stateCode,
        state: d.stateName || prev.state,
      }));
    } catch (err: any) {
      setGstError(err.response?.data?.error || 'Invalid GSTIN');
    }
  };

  if (loading) return <LoadingSpinner />;

  const inp = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]';
  const lbl = 'mb-1 block text-sm font-medium text-gray-700';

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Company Profile</h2>
          <p className="text-sm text-gray-500">Business details used on invoices and official documents.</p>
        </div>
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saved ? 'Saved!' : 'Save Changes'}
        </button>
      </div>
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div><label className={lbl}>Legal / Registered Name *</label><input className={inp} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="As per GST certificate" /></div>
          <div><label className={lbl}>Full Legal Name</label><input className={inp} value={form.legalName} onChange={e => setForm({ ...form, legalName: e.target.value })} placeholder="e.g. Mehar Enterprises Pvt Ltd" /></div>
          <div><label className={lbl}>Brand / Trade Name</label><input className={inp} value={form.brandName} onChange={e => setForm({ ...form, brandName: e.target.value })} placeholder="e.g. Sun Stellar" /><p className="mt-1 text-xs text-gray-400">Shown on proposals, invoices & customer-facing docs</p></div>
        </div>
        <div>
          <label className={lbl}>Name Display on Documents</label>
          <div className="mt-1 flex gap-3">
            {[
              { value: 'brand', label: 'Brand Name Only', desc: form.brandName || 'Sun Stellar' },
              { value: 'legal', label: 'Legal Name Only', desc: form.name || 'Mehar Enterprises' },
              { value: 'both', label: 'Both', desc: `${form.brandName || 'Sun Stellar'} + ${form.legalName || form.name || 'Mehar Enterprises'}` },
            ].map(opt => (
              <button key={opt.value} type="button" onClick={() => setForm({ ...form, displayNameMode: opt.value })}
                className={`flex-1 rounded-lg border-2 p-3 text-left transition-all ${form.displayNameMode === opt.value ? 'border-[var(--ink-0)] bg-[var(--saffron-50)]' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className="text-sm font-medium text-gray-900">{opt.label}</div>
                <div className="mt-0.5 text-xs text-gray-500">{opt.desc}</div>
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className={lbl}>GSTIN</label>
            <input className={`${inp} ${gstError ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : ''}`} value={form.gstin} onChange={e => setForm({ ...form, gstin: e.target.value.toUpperCase() })} onBlur={handleGstinBlur} placeholder="06ADPPK5843B2ZK" maxLength={15} />
            {gstError && <p className="mt-1 text-xs text-red-500">{gstError}</p>}
          </div>
          <div><label className={lbl}>PAN</label><input className={inp} value={form.pan} onChange={e => setForm({ ...form, pan: e.target.value.toUpperCase() })} placeholder="Auto-filled from GSTIN" readOnly={!!form.gstin} /></div>
          <div><label className={lbl}>State Code</label><input className={inp} value={form.stateCode} onChange={e => setForm({ ...form, stateCode: e.target.value })} placeholder="Auto-filled from GSTIN" readOnly={!!form.gstin} /></div>
        </div>
        <div className="border-t border-gray-200 pt-4">
          <h3 className="mb-3 text-sm font-semibold text-gray-700">Address</h3>
          <div className="space-y-4">
            <div><label className={lbl}>Address Line 1</label><input className={inp} value={form.line1} onChange={e => setForm({ ...form, line1: e.target.value })} /></div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div><label className={lbl}>City</label><input className={inp} value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} /></div>
              <div><label className={lbl}>State</label><input className={inp} value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} /></div>
              <div><label className={lbl}>Pincode</label><input className={inp} value={form.pincode} onChange={e => setForm({ ...form, pincode: e.target.value })} /></div>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-200 pt-4">
          <h3 className="mb-3 text-sm font-semibold text-gray-700">Contact</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div><label className={lbl}>Phone</label><input className={inp} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
            <div><label className={lbl}>Email</label><input className={inp} type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div><label className={lbl}>Website</label><input className={inp} value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} /></div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== Users & Roles Tab ====================
interface UserRecord {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string | null;
  designation?: string | null;
  employeeType?: string;
  location?: string | null;
  geoLat?: number | null;
  geoLng?: number | null;
  reportsToId?: string | null;
  reportsTo?: { id: string; firstName: string; lastName: string; email: string; designation?: string } | null;
  role: { id: string; name: string; displayName: string };
  department?: { id: string; name: string } | null;
  isActive: boolean;
  lastLoginAt?: string | null;
}

interface RoleRecord {
  id: string;
  name: string;
  displayName: string;
}

const EMPLOYEE_TYPES = [
  { value: 'office', label: 'Office' },
  { value: 'field', label: 'Field' },
  { value: 'hybrid', label: 'Hybrid' },
];

const CITY_COORDS: Record<string, { lat: number; lng: number; state: string }> = {
  'Mumbai': { lat: 19.0760, lng: 72.8777, state: 'Maharashtra' },
  'Delhi': { lat: 28.6139, lng: 77.2090, state: 'Delhi' },
  'Delhi NCR': { lat: 28.6139, lng: 77.2090, state: 'Delhi' },
  'Bangalore': { lat: 12.9716, lng: 77.5946, state: 'Karnataka' },
  'Bengaluru': { lat: 12.9716, lng: 77.5946, state: 'Karnataka' },
  'Hyderabad': { lat: 17.3850, lng: 78.4867, state: 'Telangana' },
  'Chennai': { lat: 13.0827, lng: 80.2707, state: 'Tamil Nadu' },
  'Kolkata': { lat: 22.5726, lng: 88.3639, state: 'West Bengal' },
  'Pune': { lat: 18.5204, lng: 73.8567, state: 'Maharashtra' },
  'Ahmedabad': { lat: 23.0225, lng: 72.5714, state: 'Gujarat' },
  'Jaipur': { lat: 26.9124, lng: 75.7873, state: 'Rajasthan' },
  'Lucknow': { lat: 26.8467, lng: 80.9462, state: 'Uttar Pradesh' },
  'Chandigarh': { lat: 30.7333, lng: 76.7794, state: 'Chandigarh' },
  'Panipat': { lat: 29.3909, lng: 76.9635, state: 'Haryana' },
  'Karnal': { lat: 29.6857, lng: 76.9905, state: 'Haryana' },
  'Sonipat': { lat: 28.9931, lng: 77.0151, state: 'Haryana' },
  'Gurugram': { lat: 28.4595, lng: 77.0266, state: 'Haryana' },
  'Gurgaon': { lat: 28.4595, lng: 77.0266, state: 'Haryana' },
  'Faridabad': { lat: 28.4089, lng: 77.3178, state: 'Haryana' },
  'Noida': { lat: 28.5355, lng: 77.3910, state: 'Uttar Pradesh' },
  'Ghaziabad': { lat: 28.6692, lng: 77.4538, state: 'Uttar Pradesh' },
  'Indore': { lat: 22.7196, lng: 75.8577, state: 'Madhya Pradesh' },
  'Bhopal': { lat: 23.2599, lng: 77.4126, state: 'Madhya Pradesh' },
  'Nagpur': { lat: 21.1458, lng: 79.0882, state: 'Maharashtra' },
  'Surat': { lat: 21.1702, lng: 72.8311, state: 'Gujarat' },
  'Vadodara': { lat: 22.3072, lng: 73.1812, state: 'Gujarat' },
  'Rajkot': { lat: 22.3039, lng: 70.8022, state: 'Gujarat' },
  'Coimbatore': { lat: 11.0168, lng: 76.9558, state: 'Tamil Nadu' },
  'Kochi': { lat: 9.9312, lng: 76.2673, state: 'Kerala' },
  'Thiruvananthapuram': { lat: 8.5241, lng: 76.9366, state: 'Kerala' },
  'Visakhapatnam': { lat: 17.6868, lng: 83.2185, state: 'Andhra Pradesh' },
  'Vijayawada': { lat: 16.5062, lng: 80.6480, state: 'Andhra Pradesh' },
  'Patna': { lat: 25.6093, lng: 85.1376, state: 'Bihar' },
  'Ranchi': { lat: 23.3441, lng: 85.3096, state: 'Jharkhand' },
  'Bhubaneswar': { lat: 20.2961, lng: 85.8245, state: 'Odisha' },
  'Guwahati': { lat: 26.1445, lng: 91.7362, state: 'Assam' },
  'Dehradun': { lat: 30.3165, lng: 78.0322, state: 'Uttarakhand' },
  'Amritsar': { lat: 31.6340, lng: 74.8723, state: 'Punjab' },
  'Ludhiana': { lat: 30.9010, lng: 75.8573, state: 'Punjab' },
  'Jalandhar': { lat: 31.3260, lng: 75.5762, state: 'Punjab' },
  'Agra': { lat: 27.1767, lng: 78.0081, state: 'Uttar Pradesh' },
  'Varanasi': { lat: 25.3176, lng: 82.9739, state: 'Uttar Pradesh' },
  'Kanpur': { lat: 26.4499, lng: 80.3319, state: 'Uttar Pradesh' },
  'Meerut': { lat: 28.9845, lng: 77.7064, state: 'Uttar Pradesh' },
  'Nashik': { lat: 19.9975, lng: 73.7898, state: 'Maharashtra' },
  'Aurangabad': { lat: 19.8762, lng: 75.3433, state: 'Maharashtra' },
  'Raipur': { lat: 21.2514, lng: 81.6296, state: 'Chhattisgarh' },
  'Jodhpur': { lat: 26.2389, lng: 73.0243, state: 'Rajasthan' },
  'Udaipur': { lat: 24.5854, lng: 73.7125, state: 'Rajasthan' },
  'Mysuru': { lat: 12.2958, lng: 76.6394, state: 'Karnataka' },
  'Mangaluru': { lat: 12.9141, lng: 74.8560, state: 'Karnataka' },
  'Hubli': { lat: 15.3647, lng: 75.1240, state: 'Karnataka' },
  'Shimla': { lat: 31.1048, lng: 77.1734, state: 'Himachal Pradesh' },
  'Jammu': { lat: 32.7266, lng: 74.8570, state: 'J&K' },
  'Srinagar': { lat: 34.0837, lng: 74.7973, state: 'J&K' },
  'Thane': { lat: 19.2183, lng: 72.9781, state: 'Maharashtra' },
  'Navi Mumbai': { lat: 19.0330, lng: 73.0297, state: 'Maharashtra' },
  'Rohtak': { lat: 28.8955, lng: 76.6066, state: 'Haryana' },
  'Hisar': { lat: 29.1492, lng: 75.7217, state: 'Haryana' },
  'Ambala': { lat: 30.3782, lng: 76.7767, state: 'Haryana' },
  'Panchkula': { lat: 30.6942, lng: 76.8606, state: 'Haryana' },
  'Kurukshetra': { lat: 29.9695, lng: 76.8783, state: 'Haryana' },
};

interface DeptRecord {
  id: string;
  name: string;
  code: string | null;
  headId: string | null;
  kpis: any;
  disabled?: boolean;
}

// Same rule as the server Team drawer — keep in step with backend/src/modules/teams/team-of.ts
// (SALES_TEAM_HEAD_ROLES, SALES_MEMBER_ROLES = SALES_ROLE_NAMES in telecaller/sales-roles.ts + sales_manager + lead_triage_coordinator).
const SALES_TEAM_HEADS = ['sr_sales_manager', 'sales_manager', 'manager_sales'];
const SALES_MEMBERS = [
  'telecaller', 'sales_rep', 'sales_executive', 'sales_exec', 'sales_head', 'area_manager', 'field_manager', 'field_executive',
  'manager', 'sales', 'sr_sales_manager', 'manager_sales', 'coordinator', 'sales_coordinator', 'assistant_executive_sales',
  'sales_manager', 'lead_triage_coordinator',
];

function teamCount(users: UserRecord[], id: string): number {
  const head = users.find((u) => u.id === id);
  if (!head || !SALES_TEAM_HEADS.includes((head.role?.name ?? '').toLowerCase())) return 0;
  const seen = new Set<string>([id]);
  let frontier = [id];
  while (frontier.length) {
    const next = users.filter((u) => u.reportsToId && frontier.includes(u.reportsToId) && !seen.has(u.id));
    next.forEach((u) => seen.add(u.id));
    frontier = next.map((u) => u.id);
  }
  seen.delete(id);
  return users.filter((u) => seen.has(u.id) && u.isActive && SALES_MEMBERS.includes((u.role?.name ?? '').toLowerCase())).length;
}

function UsersRolesTab() {
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [departments, setDepartments] = useState<DeptRecord[]>([]);
  const [roles, setRoles] = useState<RoleRecord[]>([]);
  const [loading, setLoading] = useState(true);
  // Edit modal state
  const [editUser, setEditUser] = useState<UserRecord | null>(null);
  const [editForm, setEditForm] = useState({
    firstName: '', lastName: '', phone: '', roleId: '', departmentId: '',
    designation: '', employeeType: 'office', location: '', geoLat: '', geoLng: '', reportsToId: '', isActive: true,
  });
  const [editSaving, setEditSaving] = useState(false);
  const [teamUser, setTeamUser] = useState<UserRecord | null>(null); // W7: Team · n drawer
  const [syncNotice, setSyncNotice] = useState<string | null>(null);

  // Filter
  const [filterDept, setFilterDept] = useState('');
  const [filterType, setFilterType] = useState('');
  const [search, setSearch] = useState('');

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, deptsRes, rolesRes] = await Promise.all([
        api.get('/auth/users'),
        api.get('/departments'),
        api.get('/auth/roles'),
      ]);
      setUsers(usersRes.data.data ?? []);
      setDepartments(deptsRes.data.data ?? []);
      setRoles(rolesRes.data.data ?? []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const openEdit = (u: UserRecord) => {
    setEditUser(u);
    setEditForm({
      firstName: u.firstName, lastName: u.lastName, phone: u.phone || '',
      roleId: u.role?.id || '', departmentId: u.department?.id || '',
      designation: u.designation || '', employeeType: u.employeeType || 'office',
      location: u.location || '', geoLat: u.geoLat != null ? String(u.geoLat) : '', geoLng: u.geoLng != null ? String(u.geoLng) : '',
      reportsToId: u.reportsToId || '', isActive: u.isActive,
    });
  };

  const handleEditSave = async () => {
    if (!editUser) return;
    try {
      setEditSaving(true);
      const patchRes = await api.patch(`/auth/users/${editUser.id}`, {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        phone: editForm.phone || null,
        roleId: editForm.roleId || undefined,
        departmentId: editForm.departmentId || null,
        designation: editForm.designation || null,
        employeeType: editForm.employeeType,
        location: editForm.location || null,
        geoLat: editForm.geoLat !== '' ? Number(editForm.geoLat) : null,
        geoLng: editForm.geoLng !== '' ? Number(editForm.geoLng) : null,
        reportsToId: editForm.reportsToId || null,
        isActive: editForm.isActive,
      });
      const mgr = users.find((u) => u.id === editForm.reportsToId);
      setSyncNotice(teamSyncNotice(patchRes?.data?.teamSync, mgr ? `${mgr.firstName} ${mgr.lastName}` : null));
      setEditUser(null);
      loadData();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update user');
    } finally { setEditSaving(false); }
  };

  const filteredUsers = users.filter(u => {
    if (filterDept && u.department?.id !== filterDept) return false;
    if (filterType && u.employeeType !== filterType) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(u.firstName + ' ' + u.lastName).toLowerCase().includes(q) &&
          !u.email.toLowerCase().includes(q) &&
          !(u.designation || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const inp = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]';
  const lbl = 'mb-1 block text-sm font-medium text-gray-700';
  const sel = 'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none';

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Users & Roles</h2>
          <p className="text-sm text-gray-500">Manage team, designations, reporting hierarchy, and access roles.</p>
        </div>
        <p className="text-sm text-gray-500">
          Logins are created from <Link className="underline" to="/employees">Employees</Link> — every login
          belongs to a person.
        </p>
      </div>

      {/* Filters */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email, designation..."
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm w-64 focus:border-[var(--ink-0)] focus:outline-none" />
        <select value={filterDept} onChange={e => setFilterDept(e.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm">
          <option value="">All Departments</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm">
          <option value="">All Types</option>
          {EMPLOYEE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <span className="text-xs text-gray-400">{filteredUsers.length} of {users.length} users</span>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              <th className="px-3 py-3">Name</th>
              <th className="px-3 py-3">Email</th>
              <th className="px-3 py-3">Designation</th>
              <th className="px-3 py-3">Type</th>
              <th className="px-3 py-3">Location</th>
              <th className="px-3 py-3">Role</th>
              <th className="px-3 py-3">Department</th>
              <th className="px-3 py-3">Reports To</th>
              <th className="px-3 py-3">Status</th>
              <th className="px-3 py-3 w-16"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredUsers.map(u => {
              const isHead = departments.some(d => d.headId === u.id);
              const typeColor = u.employeeType === 'field' ? 'bg-orange-50 text-orange-700' : u.employeeType === 'hybrid' ? 'bg-purple-50 text-purple-700' : 'bg-gray-50 text-gray-600';
              return (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2.5 font-medium text-gray-900">
                    {u.firstName} {u.lastName}
                    {isHead && <Crown size={12} className="ml-1 inline text-amber-500" />}
                    {teamCount(users, u.id) > 0 && (
                      <button type="button" onClick={() => setTeamUser(u)} className="ml-2 text-xs font-normal text-[var(--saffron-600)] hover:underline">
                        Team · {teamCount(users, u.id)} ›
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-gray-600 text-xs">{u.email}</td>
                  <td className="px-3 py-2.5 text-gray-700">{u.designation || <span className="text-gray-300">—</span>}</td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${typeColor}`}>
                      {(u.employeeType || 'office').charAt(0).toUpperCase() + (u.employeeType || 'office').slice(1)}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-gray-600 text-xs">{u.location || <span className="text-gray-300">—</span>}</td>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                      {u.role?.displayName || u.role?.name || '-'}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-gray-600 text-xs">{u.department?.name || '-'}</td>
                  <td className="px-3 py-2.5 text-gray-600 text-xs">
                    {u.reportsTo ? `${u.reportsTo.firstName} ${u.reportsTo.lastName}` : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${u.isActive ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {u.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <button onClick={() => openEdit(u)} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                      <Pencil size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {filteredUsers.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-sm text-gray-400">No users found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {syncNotice && <div className="mb-3 rounded-lg bg-[#fdf6ec] px-3 py-2 text-sm text-[#c2600f]" role="status">{syncNotice}</div>}
      {teamUser && (
        <UserTeamDrawer
          userId={teamUser.id}
          name={`${teamUser.firstName} ${teamUser.lastName}`}
          designation={teamUser.designation}
          reportsToName={teamUser.reportsTo ? `${teamUser.reportsTo.firstName} ${teamUser.reportsTo.lastName}` : null}
          onEdit={() => { const u = teamUser; setTeamUser(null); openEdit(u); }}
          onClose={() => setTeamUser(null)}
        />
      )}

      {/* Edit User Modal */}
      {editUser && (
        <Modal open onClose={() => setEditUser(null)} className="!max-w-lg !bg-white">
          <div>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 sticky top-0 z-10 bg-white rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Edit User — {editUser.firstName} {editUser.lastName}</h2>
              <RequestCloseButton  className="text-gray-400 hover:text-gray-600"><X size={20} /></RequestCloseButton>
            </div>
            <div className="space-y-4 px-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className={lbl}>First Name</label><input type="text" value={editForm.firstName} onChange={e => setEditForm({ ...editForm, firstName: e.target.value })} className={inp} /></div>
                <div><label className={lbl}>Last Name</label><input type="text" value={editForm.lastName} onChange={e => setEditForm({ ...editForm, lastName: e.target.value })} className={inp} /></div>
              </div>
              <div><label className={lbl}>Phone</label><input type="text" value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} className={inp} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={lbl}>Role</label>
                  <select value={editForm.roleId} onChange={e => setEditForm({ ...editForm, roleId: e.target.value })} className={sel}>
                    {roles.map(r => <option key={r.id} value={r.id}>{r.displayName}</option>)}
                  </select>
                </div>
                <div>
                  <label className={lbl}>Department</label>
                  <select value={editForm.departmentId} onChange={e => setEditForm({ ...editForm, departmentId: e.target.value })} className={sel}>
                    <option value="">None</option>
                    {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className={lbl}>Designation</label><input type="text" value={editForm.designation} onChange={e => setEditForm({ ...editForm, designation: e.target.value })} placeholder="e.g. Sales Head, Field Executive" className={inp} /></div>
                <div>
                  <label className={lbl}>Employee Type</label>
                  <select value={editForm.employeeType} onChange={e => setEditForm({ ...editForm, employeeType: e.target.value })} className={sel}>
                    {EMPLOYEE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className={lbl}>Location</label>
                <div className="relative">
                  <input
                    type="text"
                    list="city-suggestions"
                    value={editForm.location}
                    onChange={e => {
                      const city = e.target.value;
                      const coords = CITY_COORDS[city];
                      if (coords) {
                        setEditForm({ ...editForm, location: city, geoLat: String(coords.lat), geoLng: String(coords.lng) });
                      } else {
                        setEditForm({ ...editForm, location: city });
                      }
                    }}
                    placeholder="Type city name..."
                    className={inp}
                  />
                  <datalist id="city-suggestions">
                    {Object.keys(CITY_COORDS).map(city => (
                      <option key={city} value={city}>{city}, {CITY_COORDS[city].state}</option>
                    ))}
                  </datalist>
                </div>
                {editForm.geoLat && editForm.geoLng && (
                  <p className="mt-1 text-xs text-gray-400">📍 {editForm.geoLat}, {editForm.geoLng}</p>
                )}
              </div>
              <div>
                <label className={lbl}>Reports To</label>
                <select value={editForm.reportsToId} onChange={e => setEditForm({ ...editForm, reportsToId: e.target.value })} className={sel}>
                  <option value="">None (Top-level)</option>
                  {users.filter(u => u.isActive && u.id !== editUser.id).map(u => (
                    <option key={u.id} value={u.id}>{u.firstName} {u.lastName} {u.designation ? `(${u.designation})` : ''}</option>
                  ))}
                </select>
                <ReportsToNote
                  personName={`${editForm.firstName} ${editForm.lastName}`.trim() || editUser.email}
                  managerName={(() => { const m = users.find((x) => x.id === editForm.reportsToId); return m ? `${m.firstName} ${m.lastName}` : null; })()}
                />
              </div>
              <div className="flex items-center gap-3">
                <label className={lbl + ' mb-0'}>Active</label>
                <button onClick={() => setEditForm({ ...editForm, isActive: !editForm.isActive })} className="text-gray-500">
                  {editForm.isActive ? <ToggleRight size={28} className="text-green-600" /> : <ToggleLeft size={28} className="text-gray-400" />}
                </button>
                <span className="text-sm text-gray-500">{editForm.isActive ? 'Active' : 'Inactive'}</span>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <RequestCloseButton  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</RequestCloseButton>
              <button onClick={handleEditSave} disabled={editSaving || !editForm.firstName || !editForm.lastName}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
                {editSaving && <Loader2 size={14} className="animate-spin" />} Save Changes
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ==================== Departments Tab ====================
function DepartmentsTab() {
  const [departments, setDepartments] = useState<DeptRecord[]>([]);
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [expandedDept, setExpandedDept] = useState<string | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const [deptsRes, usersRes] = await Promise.all([
        api.get('/departments'),
        api.get('/auth/users'),
      ]);
      setDepartments(deptsRes.data.data ?? []);
      setUsers(usersRes.data.data ?? []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const setDeptHead = async (deptId: string, headId: string | null) => {
    try {
      setSaving(deptId);
      await api.patch(`/departments/${deptId}`, { headId: headId || null });
      setDepartments(prev => prev.map(d => d.id === deptId ? { ...d, headId } : d));
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update department head');
    } finally { setSaving(null); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Departments</h2>
        <p className="text-sm text-gray-500">Manage departments and assign department heads. Heads can monitor their team.</p>
      </div>

      <div className="space-y-3">
        {departments.map(dept => {
          const deptUsers = users.filter(u => u.department?.id === dept.id);
          const head = dept.headId ? users.find(u => u.id === dept.headId) : null;
          const isExpanded = expandedDept === dept.id;

          return (
            <div key={dept.id} className="rounded-lg border border-gray-200 bg-white">
              <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedDept(isExpanded ? null : dept.id)}>
                <div className="flex items-center gap-3">
                  <div>
                    <h3 className="font-medium text-gray-900">{dept.name}</h3>
                    <p className="text-xs text-gray-500">{dept.code || ''} · {deptUsers.length} member{deptUsers.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {head ? (
                    <span className="flex items-center gap-1 text-sm text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                      <Crown size={12} /> {head.firstName} {head.lastName}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">No head assigned</span>
                  )}
                  <label className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={!dept.disabled}
                      disabled={saving === dept.id}
                      onChange={async (e) => {
                        // Disable, never delete. Manak kept all 12 departments (2026-08-24) and
                        // switches off the ones he does not use: staff, history and the
                        // department's login policy all survive a disable. A disabled department
                        // stops appearing in the employee pickers, which send ?activeOnly=true.
                        setSaving(dept.id);
                        try { await api.patch(`/departments/${dept.id}`, { disabled: !e.target.checked }); await loadData(); }
                        finally { setSaving(null); }
                      }}
                    />
                    <span className="text-xs text-gray-600">{dept.disabled ? 'Off' : 'On'}</span>
                  </label>
                  {isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                </div>
              </div>

              {isExpanded && (
                <div className="border-t border-gray-100 px-4 py-3">
                  <div className="mb-3">
                    <label className="mb-1 block text-sm font-medium text-gray-700">Department Head</label>
                    <div className="flex items-center gap-2">
                      <select
                        value={dept.headId || ''}
                        onChange={e => setDeptHead(dept.id, e.target.value || null)}
                        className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                        disabled={saving === dept.id}
                      >
                        <option value="">No head</option>
                        {deptUsers.map(u => (
                          <option key={u.id} value={u.id}>{u.firstName} {u.lastName} ({u.role?.displayName || u.role?.name})</option>
                        ))}
                      </select>
                      {saving === dept.id && <Loader2 size={16} className="animate-spin text-gray-400" />}
                    </div>
                  </div>

                  {deptUsers.length > 0 ? (
                    <div>
                      <h4 className="mb-2 text-xs font-medium uppercase text-gray-500">Team Members</h4>
                      <div className="space-y-1">
                        {deptUsers.map(u => (
                          <div key={u.id} className="flex items-center justify-between rounded px-2 py-1.5 hover:bg-gray-50">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-900">{u.firstName} {u.lastName}</span>
                              {dept.headId === u.id && <Crown size={11} className="text-amber-500" />}
                            </div>
                            <span className="text-xs text-gray-500">{u.role?.displayName || u.role?.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">No members in this department yet.</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ==================== General Tab ====================
const TIMEZONE_OPTIONS = [
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST, UTC+5:30)' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai (GST, UTC+4)' },
  { value: 'Asia/Dhaka', label: 'Asia/Dhaka (BST, UTC+6)' },
  { value: 'Asia/Kathmandu', label: 'Asia/Kathmandu (NPT, UTC+5:45)' },
  { value: 'Asia/Colombo', label: 'Asia/Colombo (IST, UTC+5:30)' },
  { value: 'Asia/Karachi', label: 'Asia/Karachi (PKT, UTC+5)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT, UTC+8)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST, UTC+9)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST, UTC+8)' },
  { value: 'Asia/Riyadh', label: 'Asia/Riyadh (AST, UTC+3)' },
  { value: 'Africa/Nairobi', label: 'Africa/Nairobi (EAT, UTC+3)' },
  { value: 'Africa/Lagos', label: 'Africa/Lagos (WAT, UTC+1)' },
  { value: 'Europe/London', label: 'Europe/London (GMT/BST, UTC+0/+1)' },
  { value: 'America/New_York', label: 'America/New_York (EST, UTC-5)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST, UTC-8)' },
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
];

function GeneralTab() {
  const [recordingConfig, setRecordingConfig] = useState({ callRecordingEnabled: false, recordingRetentionDays: 30 });
  const [recordingLoading, setRecordingLoading] = useState(true);
  const [recordingSaving, setRecordingSaving] = useState(false);
  const [timezone, setTimezone] = useState('Asia/Kolkata');
  const [tzLoading, setTzLoading] = useState(true);
  const [tzSaving, setTzSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [recRes, tzRes] = await Promise.all([
          api.get('/telecaller/recording-config'),
          api.get('/settings/timezone'),
        ]);
        if (recRes.data?.data) setRecordingConfig(recRes.data.data);
        if (tzRes.data?.data?.value) setTimezone(tzRes.data.data.value);
      } catch {}
      setRecordingLoading(false);
      setTzLoading(false);
    })();
  }, []);

  const saveTimezone = async (tz: string) => {
    const prev = timezone;
    setTimezone(tz);
    setTzSaving(true);
    try {
      await api.put('/settings/timezone', { value: tz });
    } catch {
      setTimezone(prev);
      alert('Failed to save timezone');
    }
    setTzSaving(false);
  };

  const saveRecordingConfig = async (updates: Partial<typeof recordingConfig>) => {
    const prev = { ...recordingConfig };
    const newConfig = { ...recordingConfig, ...updates };
    setRecordingConfig(newConfig);
    setRecordingSaving(true);
    try {
      await api.put('/telecaller/recording-config', newConfig);
    } catch (err: any) {
      // Revert on failure
      setRecordingConfig(prev);
      alert(err.response?.data?.error || 'Failed to save recording config');
    }
    setRecordingSaving(false);
  };

  return (
    <div>
      <h2 className="mb-4 text-lg font-semibold text-gray-900">General Settings</h2>
      <p className="mb-6 text-sm text-gray-500">System-wide defaults and configuration.</p>
      <div className="space-y-4">
        <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-gray-900">Default Currency</p>
            <p className="text-xs text-gray-500">Used for all financial transactions</p>
          </div>
          <span className="rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700">INR</span>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-gray-900">Date Format</p>
            <p className="text-xs text-gray-500">Display format across the application</p>
          </div>
          <span className="rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700">DD/MM/YYYY</span>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-gray-900">Financial Year Start</p>
            <p className="text-xs text-gray-500">Beginning of the financial year</p>
          </div>
          <span className="rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700">April</span>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-gray-900">Timezone</p>
            <p className="text-xs text-gray-500">Display timezone for dates across the application</p>
          </div>
          {tzLoading ? (
            <span className="text-sm text-gray-400">Loading...</span>
          ) : (
            <select
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none"
              value={timezone}
              onChange={e => saveTimezone(e.target.value)}
              disabled={tzSaving}
            >
              {TIMEZONE_OPTIONS.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          )}
        </div>

        {/* Call Recording Section */}
        <div className="mt-6 rounded-lg border border-gray-200 p-4">
          <h3 className="mb-3 text-sm font-semibold text-gray-900">Call Recording</h3>
          {recordingLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">Enable Call Recording</p>
                  <p className="text-xs text-gray-500">Record all AURA Sales App calls and store on server</p>
                </div>
                <button onClick={() => saveRecordingConfig({ callRecordingEnabled: !recordingConfig.callRecordingEnabled })} disabled={recordingSaving} className="text-gray-400 hover:text-[var(--ink-0)]">
                  {recordingConfig.callRecordingEnabled
                    ? <ToggleRight className="h-8 w-8 text-[var(--ink-0)]" />
                    : <ToggleLeft className="h-8 w-8" />}
                </button>
              </div>
              {recordingConfig.callRecordingEnabled && (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Retention Period</p>
                    <p className="text-xs text-gray-500">How long to keep recordings before auto-delete</p>
                  </div>
                  <select
                    className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                    value={recordingConfig.recordingRetentionDays}
                    onChange={e => saveRecordingConfig({ recordingRetentionDays: Number(e.target.value) })}
                  >
                    <option value={7}>7 Days</option>
                    <option value={15}>15 Days</option>
                    <option value={30}>30 Days</option>
                    <option value={0}>Forever</option>
                  </select>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== Modules Tab ====================
interface ModuleRecord {
  id: string;
  moduleName: string;
  isEnabled: boolean;
}

function ModulesTab() {
  const [modules, setModules] = useState<ModuleRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/settings/modules');
        setModules(res.data.data ?? []);
      } catch {}
      setLoading(false);
    })();
  }, []);

  const refreshTenant = useAuthStore((s) => s.refreshTenant);

  const handleToggle = async (mod: ModuleRecord) => {
    setToggling(mod.id);
    try {
      await api.put(`/settings/modules/${mod.id}`, { isEnabled: !mod.isEnabled });
      setModules(prev => prev.map(m => m.id === mod.id ? { ...m, isEnabled: !m.isEnabled } : m));
      // Refresh tenant config so sidebar updates immediately
      await refreshTenant();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update');
    }
    setToggling(null);
  };

  if (loading) return <LoadingSpinner />;

  const moduleLabels: Record<string, string> = {
    crm: 'CRM', accounts: 'Accounts', sales: 'Sales', finance: 'Finance',
    accounting: 'Accounting', projects: 'Projects', procurement: 'Procurement',
    warehouse: 'Warehouse', 'service-mgmt': 'Service Management',
    automation: 'Automation', analytics: 'Analytics', hr: 'HR', ai: 'AI Assistant',
  };

  return (
    <div>
      <h2 className="mb-4 text-lg font-semibold text-gray-900">Modules</h2>
      <p className="mb-6 text-sm text-gray-500">Enable or disable modules. Disabled modules will be hidden from navigation.</p>
      <div className="space-y-2">
        {modules.map(mod => (
          <div key={mod.id} className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-gray-900">{moduleLabels[mod.moduleName] || mod.moduleName}</p>
              <p className="text-xs text-gray-500">{mod.moduleName}</p>
            </div>
            <button
              onClick={() => handleToggle(mod)}
              disabled={toggling === mod.id}
              className="text-gray-500 transition-colors hover:text-[var(--ink-0)]"
            >
              {mod.isEnabled
                ? <ToggleRight size={28} className="text-[var(--ink-0)]" />
                : <ToggleLeft size={28} className="text-gray-400" />
              }
            </button>
          </div>
        ))}
        {modules.length === 0 && <p className="py-8 text-center text-sm text-gray-400">No modules configured.</p>}
      </div>
    </div>
  );
}

// ==================== Shift Management Section ====================
function ShiftManagementSection() {
  const [shiftConfig, setShiftConfig] = useState<any>({
    workingDays: [1, 2, 3, 4, 5, 6],
    shifts: [{ shiftNumber: 1, shiftName: 'General', startTime: '08:00', endTime: '17:00', breakMinutes: 30, isActive: true }],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/manufacturing-p0/shift/config');
        if (res.data.data) {
          const d = res.data.data;
          setShiftConfig({
            workingDays: d.workingDays || [1, 2, 3, 4, 5, 6],
            shifts: d.shifts?.length ? d.shifts : [
              { shiftNumber: 1, shiftName: d.defaultStartTime ? 'General' : 'General', startTime: d.defaultStartTime || '08:00', endTime: d.defaultEndTime || '17:00', breakMinutes: d.breakMinutes ?? 30, isActive: true }
            ],
            autoStopEnabled: d.autoStopEnabled ?? false,
          });
        }
      } catch { }
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch('/manufacturing-p0/shift/config', shiftConfig);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { }
    setSaving(false);
  };

  const addShift = () => {
    const next = shiftConfig.shifts.length + 1;
    if (next > 5) return;
    const lastEnd = shiftConfig.shifts[shiftConfig.shifts.length - 1]?.endTime || '17:00';
    setShiftConfig((c: any) => ({
      ...c,
      shifts: [...c.shifts, { shiftNumber: next, shiftName: `Shift ${next}`, startTime: lastEnd, endTime: '23:00', breakMinutes: 30, isActive: true }],
    }));
  };

  const removeShift = (idx: number) => {
    if (shiftConfig.shifts.length <= 1) return;
    setShiftConfig((c: any) => ({
      ...c,
      shifts: c.shifts.filter((_: any, i: number) => i !== idx).map((s: any, i: number) => ({ ...s, shiftNumber: i + 1 })),
    }));
  };

  const updateShift = (idx: number, field: string, value: any) => {
    setShiftConfig((c: any) => ({
      ...c,
      shifts: c.shifts.map((s: any, i: number) => i === idx ? { ...s, [field]: value } : s),
    }));
  };

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const toggleDay = (day: number) => {
    setShiftConfig((c: any) => ({
      ...c,
      workingDays: c.workingDays.includes(day) ? c.workingDays.filter((d: number) => d !== day) : [...c.workingDays, day].sort(),
    }));
  };

  if (loading) return <div className="py-4"><Loader2 size={14} className="animate-spin inline" /> Loading shift config...</div>;

  return (
    <div className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-[var(--text-primary)]">Shift Management</h3>
          <p className="text-xs text-[var(--text-muted)]">Configure daily shifts — supports multiple shifts per day</p>
        </div>
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-1 rounded-lg bg-[var(--ink-0)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
          <Save size={12} />
          {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Shifts'}
        </button>
      </div>

      {/* Working Days */}
      <div>
        <label className="text-xs font-medium text-[var(--text-secondary)] mb-2 block">Working Days</label>
        <div className="flex gap-1.5">
          {dayNames.map((name, i) => (
            <button
              key={i}
              onClick={() => toggleDay(i)}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                shiftConfig.workingDays.includes(i)
                  ? 'bg-[var(--ink-0)] text-white'
                  : 'bg-[var(--bg-surface-hover)] text-[var(--text-muted)]'
              }`}
            >
              {name}
            </button>
          ))}
        </div>
      </div>

      {/* Shift Slots */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-[var(--text-secondary)]">Shift Slots ({shiftConfig.shifts.length})</label>
          {shiftConfig.shifts.length < 5 && (
            <button onClick={addShift} className="flex items-center gap-1 rounded-lg bg-[var(--bg-surface-hover)] px-2.5 py-1 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
              <Plus size={12} /> Add Shift
            </button>
          )}
        </div>

        {shiftConfig.shifts.map((shift: any, idx: number) => (
          <div key={idx} className={`rounded-lg border p-3 space-y-2 ${shift.isActive ? 'border-[var(--ink-0)]/30 bg-[var(--saffron-50)]/5' : 'border-[var(--border-primary)] opacity-60'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-[var(--ink-0)] text-white text-xs font-bold">{shift.shiftNumber}</span>
                <input
                  value={shift.shiftName}
                  onChange={e => updateShift(idx, 'shiftName', e.target.value)}
                  className="bg-transparent border-b border-[var(--border-primary)] text-sm font-medium text-[var(--text-primary)] focus:outline-none focus:border-[var(--ink-0)] w-32"
                  placeholder="Shift name"
                />
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => updateShift(idx, 'isActive', !shift.isActive)} className="text-xl">
                  {shift.isActive ? <ToggleRight size={22} className="text-[var(--saffron-600)]" /> : <ToggleLeft size={22} className="text-gray-400" />}
                </button>
                {shiftConfig.shifts.length > 1 && (
                  <button onClick={() => removeShift(idx)} className="text-red-400 hover:text-red-500">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="text-[10px] text-[var(--text-muted)]">Start</label>
                <input
                  type="time"
                  value={shift.startTime}
                  onChange={e => updateShift(idx, 'startTime', e.target.value)}
                  className="w-full rounded border border-[var(--border-primary)] bg-[var(--bg-surface)] px-2 py-1 text-xs"
                />
              </div>
              <div className="flex-1">
                <label className="text-[10px] text-[var(--text-muted)]">End</label>
                <input
                  type="time"
                  value={shift.endTime}
                  onChange={e => updateShift(idx, 'endTime', e.target.value)}
                  className="w-full rounded border border-[var(--border-primary)] bg-[var(--bg-surface)] px-2 py-1 text-xs"
                />
              </div>
              <div className="w-20">
                <label className="text-[10px] text-[var(--text-muted)]">Break (min)</label>
                <input
                  type="number"
                  value={shift.breakMinutes}
                  onChange={e => updateShift(idx, 'breakMinutes', Number(e.target.value))}
                  className="w-full rounded border border-[var(--border-primary)] bg-[var(--bg-surface)] px-2 py-1 text-xs"
                  min={0}
                  max={120}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== Discount Policy Tab ====================
// ==================== Manufacturing Settings Tab ====================
export function ManufacturingSettingsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [config, setConfig] = useState<any>({});

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/manufacturing-flow/config');
        setConfig(res.data.data?.config || {});
      } catch {
        // If no config, use defaults
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/manufacturing-flow/config', config);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { }
    setSaving(false);
  };

  const toggle = (key: string) => setConfig((c: any) => ({ ...c, [key]: !c[key] }));

  if (loading) return <div className="flex items-center gap-2 py-8"><Loader2 size={16} className="animate-spin" /> Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">Manufacturing Settings</h2>
          <p className="text-sm text-[var(--text-muted)]">Configure production pipeline for your tenant</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
        >
          <Save size={14} />
          {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
        </button>
      </div>

      {/* Pipeline Mode */}
      <div className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] p-4">
        <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">Pipeline Mode</h3>
        <div className="flex gap-4">
          {(['full', 'simplified'] as const).map(mode => (
            <label key={mode} className={`flex-1 cursor-pointer rounded-lg border-2 p-3 transition-all ${
              config.pipelineMode === mode ? 'border-[var(--ink-0)] bg-[var(--saffron-50)]' : 'border-[var(--border-primary)]'
            }`}>
              <input
                type="radio"
                name="pipelineMode"
                className="sr-only"
                checked={config.pipelineMode === mode}
                onChange={() => setConfig((c: any) => ({
                  ...c,
                  pipelineMode: mode,
                  activeStages: mode === 'simplified' ? [0, 1, 12, 13, 14, 15] : [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15],
                }))}
              />
              <p className="text-sm font-medium text-[var(--text-primary)] capitalize">{mode}</p>
              <p className="text-xs text-[var(--text-muted)]">
                {mode === 'full' ? '16 stages — full manufacturing pipeline' : '6 stages — RM, Production, QC-1/2/3, Dispatch'}
              </p>
            </label>
          ))}
        </div>
      </div>

      {/* Toggles */}
      <div className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] p-4 space-y-4">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Module Toggles</h3>

        {[
          { key: 'laborEnabled', label: 'Labor Management', desc: 'Track manpower allocation per shift' },
          { key: 'shiftGateEnabled', label: 'Shift Gate', desc: 'Require active shift to advance orders' },
          { key: 'showPreviousDayOrders', label: 'Previous Day Orders', desc: 'Show pending orders from previous day on floor' },
          { key: 'soLinkVisibility', label: 'SO Link in Planning', desc: 'Show linked Sales Order in production planning' },
        ].map(({ key, label, desc }) => (
          <div key={key} className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[var(--text-primary)]">{label}</p>
              <p className="text-xs text-[var(--text-muted)]">{desc}</p>
            </div>
            <button onClick={() => toggle(key)} className="text-2xl">
              {config[key] ? <ToggleRight size={28} className="text-[var(--saffron-600)]" /> : <ToggleLeft size={28} className="text-gray-400" />}
            </button>
          </div>
        ))}
        {/* Phase 2 (decisions 4, 5, 7): read-only — these follow the locked decisions, not a switch. */}
        {[
          { label: 'Allow skipping RM inspection', value: 'Off · RM master flag only' },
          { label: 'FG activation without QA pass', value: 'Blocked · stock comes only from the QA packing pass' },
          { label: 'Serial rules {CAT}-{GRADE}-{SIZE}-{YY}-{seq}', value: 'One per category / grade / size — loaded with the serial rules import' },
        ].map(({ label, value }) => (
          <div key={label} className="flex items-center justify-between">
            <p className="text-sm font-medium text-[var(--text-primary)]">{label}</p>
            <span className="text-xs text-[var(--text-muted)]">{value}</span>
          </div>
        ))}
      </div>

      {/* Barcode & RM */}
      <div className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] p-4 space-y-4">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Barcode & Raw Materials</h3>

        <div>
          <label className="text-xs font-medium text-[var(--text-secondary)]">Barcode Generation Timing</label>
          <select
            value={config.barcodeGenerationTiming || 'on_stage_11_to_12'}
            onChange={e => setConfig((c: any) => ({ ...c, barcodeGenerationTiming: e.target.value }))}
            className="mt-1 w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm"
          >
            <option value="on_plan_start">At plan start (simplified)</option>
            <option value="on_stage_11_to_12">At stage 11 → 12 transition (full)</option>
          </select>
        </div>

        <div>
          <label className="text-xs font-medium text-[var(--text-secondary)]">RM Consumption Mode</label>
          <select
            value={config.rmConsumptionMode || 'consume'}
            onChange={e => setConfig((c: any) => ({ ...c, rmConsumptionMode: e.target.value }))}
            className="mt-1 w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm"
          >
            <option value="hold">Hold (reserve qty, release on barcode delete)</option>
            <option value="consume">Consume (immediate inventory decrement)</option>
          </select>
        </div>
      </div>

      {/* QC Settings */}
      <div className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] p-4 space-y-4">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Quality Control</h3>

        <div>
          <label className="text-xs font-medium text-[var(--text-secondary)]">QC Default Result</label>
          <select
            value={config.qcDefaults?.defaultResult === 'fail' ? 'fail' : 'explicit'}
            onChange={e => setConfig((c: any) => ({
              ...c, qcDefaults: { ...c.qcDefaults, defaultResult: e.target.value }
            }))}
            className="mt-1 w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm"
          >
            <option value="explicit">Explicit pass at gates (readings required)</option>
            <option value="fail">Fail-first (must explicitly pass each item)</option>
          </select>
        </div>

        {[
          { key: 'failPhotoRequired', label: 'Mandatory Photo on Fail', parentKey: 'qcDefaults' },
          { key: 'failProductDropdown', label: 'Product Dropdown on Fail', parentKey: 'qcDefaults' },
        ].map(({ key, label, parentKey }) => (
          <div key={key} className="flex items-center justify-between">
            <p className="text-sm font-medium text-[var(--text-primary)]">{label}</p>
            <button onClick={() => setConfig((c: any) => ({
              ...c, [parentKey]: { ...c[parentKey], [key]: !c[parentKey]?.[key] }
            }))} className="text-2xl">
              {config[parentKey]?.[key] ? <ToggleRight size={28} className="text-[var(--saffron-600)]" /> : <ToggleLeft size={28} className="text-gray-400" />}
            </button>
          </div>
        ))}

        <div>
          <label className="text-xs font-medium text-[var(--text-secondary)]">QC Timeout Notification (hours)</label>
          <input
            type="number"
            value={config.qcTimeoutHours ?? 24}
            onChange={e => setConfig((c: any) => ({ ...c, qcTimeoutHours: Number(e.target.value) }))}
            className="mt-1 w-24 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm"
            min={1}
            max={168}
          />
          <p className="text-xs text-[var(--text-muted)] mt-1">Alert when barcode generated but QC not done within this time</p>
        </div>
      </div>

      {/* Shift Management */}
      <ShiftManagementSection />

      {/* QC Fail Actions */}
      <div className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] p-4 space-y-4">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">QC Fail Actions</h3>

        <div>
          <label className="text-xs font-medium text-[var(--text-secondary)]">QC-1 Fail Action</label>
          <select
            value={config.qc1FailAction || 'manual'}
            onChange={e => setConfig((c: any) => ({ ...c, qc1FailAction: e.target.value }))}
            className="mt-1 w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm"
          >
            <option value="decompose_bom">Decompose BOM — release reusable materials</option>
            <option value="manual">Manual — operator decides</option>
          </select>
        </div>

        <div>
          <label className="text-xs font-medium text-[var(--text-secondary)]">QC-2 Fail Action</label>
          <select
            value={config.qc2FailAction || 'manual'}
            onChange={e => setConfig((c: any) => ({ ...c, qc2FailAction: e.target.value }))}
            className="mt-1 w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm"
          >
            <option value="case_handling">Case Handling — escalate for review</option>
            <option value="manual">Manual — operator decides</option>
          </select>
          <p className="text-xs text-amber-600 mt-1">Note: Case handling specifics TBD — ask client for details</p>
        </div>
      </div>
    </div>
  );
}

function DiscountPolicyTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [maxDocumentDiscountPct, setMaxDocumentDiscountPct] = useState<number>(10);
  const [defaultDiscountInputMode, setDefaultDiscountInputMode] = useState<'amount' | 'percentage'>('amount');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/settings/discount-policy');
        const p = res.data.data;
        if (p) {
          if (p.maxDocumentDiscountPct != null) setMaxDocumentDiscountPct(Number(p.maxDocumentDiscountPct));
          if (p.defaultDiscountInputMode) setDefaultDiscountInputMode(p.defaultDiscountInputMode);
        }
      } catch {}
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.put('/settings/discount-policy', {
        maxDocumentDiscountPct,
        defaultDiscountInputMode,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save discount policy');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  const inp = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]';

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Discount Policy</h2>
          <p className="text-sm text-gray-500">Configure discount limits and defaults for proposals, sales orders, and invoices.</p>
        </div>
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saved ? 'Saved!' : 'Save Changes'}
        </button>
      </div>

      <div className="space-y-6">
        <div className="max-w-md">
          <label className="mb-1 block text-sm font-medium text-gray-700">Max Document Discount %</label>
          <p className="mb-2 text-xs text-gray-500">Maximum percentage discount allowed on a document (proposal, SO, invoice) without requiring approval.</p>
          <input
            type="number"
            min={0}
            max={100}
            step={1}
            value={maxDocumentDiscountPct}
            onChange={e => setMaxDocumentDiscountPct(parseFloat(e.target.value) || 0)}
            className={inp}
          />
        </div>

        <div className="max-w-md">
          <label className="mb-1 block text-sm font-medium text-gray-700">Default Discount Input Mode</label>
          <p className="mb-2 text-xs text-gray-500">When adding a document-level discount, which mode should be shown by default?</p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setDefaultDiscountInputMode('amount')}
              className={`flex-1 rounded-lg border-2 p-3 text-center transition-all ${
                defaultDiscountInputMode === 'amount'
                  ? 'border-[var(--ink-0)] bg-[var(--saffron-50)]'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-sm font-medium text-gray-900">&#8377; Amount</div>
              <div className="mt-0.5 text-xs text-gray-500">Enter discount in rupees</div>
            </button>
            <button
              type="button"
              onClick={() => setDefaultDiscountInputMode('percentage')}
              className={`flex-1 rounded-lg border-2 p-3 text-center transition-all ${
                defaultDiscountInputMode === 'percentage'
                  ? 'border-[var(--ink-0)] bg-[var(--saffron-50)]'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="text-sm font-medium text-gray-900">% Percentage</div>
              <div className="mt-0.5 text-xs text-gray-500">Enter discount as percentage</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== Loading Spinner ====================
function LoadingSpinner() {
  return (
    <div className="flex justify-center py-12">
      <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
    </div>
  );
}

// ==================== Main Settings Page ====================
export function SettingsPage() {
  const { user } = useAuthStore();
  // Defense in depth — router gate in App.tsx redirects strict-view roles
  // away from /settings, but a stale bundle or direct render path should also
  // bounce them out. lead_triage_coordinator has zero settings business.
  if (user?.role?.name === 'lead_triage_coordinator') {
    return <Navigate to="/leads/triage" replace />;
  }
  const isSuperAdmin = user?.email === 'admin@aura.ardhanex.info' || user?.email === 'admin@aura.ardhanex.com';
  const isAdmin = user?.role?.name === 'admin';
  // Integrations visible to all admins; Tenants only to super admin
  const TABS = useMemo(() => ALL_TABS.filter(t => {
    if (!t.adminOnly) return true;
    if (t.id === 'tenants') return isSuperAdmin;
    return isAdmin; // Integrations tab visible to all admin users
  }), [isSuperAdmin, isAdmin]);
  // `/settings/users` opens on Users & Roles (the sidebar's Settings row).
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => {
    const t = location.pathname.split('/')[2];
    return ALL_TABS.some((x) => x.id === t) ? t : 'general';
  });
  const [connections, setConnections] = useState<any[]>([]);
  const [tenants, setTenants] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'ecosystem') loadConnections();
    if (activeTab === 'tenants') loadTenants();
  }, [activeTab]);

  const loadConnections = async () => {
    setLoading(true);
    try {
      const res = await api.get('/ecosystem/connections');
      setConnections(res.data.data);
    } catch {} finally { setLoading(false); }
  };

  const loadTenants = async () => {
    setLoading(true);
    try {
      const res = await api.get('/tenants');
      setTenants(res.data.data);
    } catch {} finally { setLoading(false); }
  };

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold text-gray-900">Settings</h1>
      <div className="flex gap-6">
        {/* Tabs */}
        <div className="w-56 shrink-0">
          <nav className="space-y-1">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  activeTab === tab.id ? 'bg-[var(--saffron-50)] text-[var(--ink-0)]' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 rounded-xl border border-gray-200 bg-white p-6">
          {activeTab === 'general' && <GeneralTab />}
          {activeTab === 'company' && <CompanyProfileTab />}
          {activeTab === 'users' && <><UsersRolesTab /><KnowledgeAccessSection /></>}
          {activeTab === 'departments' && <><DepartmentsTab /><PositionsSection /></>}
          {activeTab === 'modules' && <ModulesTab />}
          {activeTab === 'modules-explorer' && <ModulesExplorer />}
          {activeTab === 'pipelines' && <PipelinesTab />}
          {activeTab === 'lead-automation' && <LeadAutomationTab />}
          {activeTab === 'sales-teams' && <SalesTeamsTab />}
          {activeTab === 'discount-policy' && <DiscountPolicyTab />}
          {activeTab === 'manufacturing' && <ManufacturingSettingsTab />}
          {activeTab === 'gst-compliance' && <GSTComplianceTab />}
          {activeTab === 'sim-registry' && <SimRegistryTab />}

          {activeTab === 'ecosystem' && (
            <div className="space-y-6">
              <div>
                <h2 className="mb-4 text-lg font-semibold">Ardhanex Ecosystem</h2>
                <p className="mb-6 text-sm text-gray-500">Connect AURA to other Ardhanex products for unified operations.</p>
                {loading ? (
                  <LoadingSpinner />
                ) : (
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {connections.map((c: any) => (
                      <div key={c.product} className="rounded-lg border border-gray-200 p-4">
                        <div className="mb-2 flex items-center justify-between">
                          <h3 className="font-semibold text-gray-900">{c.name}</h3>
                          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${c.connected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {c.connected ? 'Connected' : 'Not connected'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">{c.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ChatKart Bridge — admin-minted PAT for ChatKart's Add-Workspace wizard */}
              <ChatKartBridgeCard />
            </div>
          )}

          {activeTab === 'tenants' && (
            <div>
              <h2 className="mb-4 text-lg font-semibold">Tenant Management</h2>
              <p className="mb-6 text-sm text-gray-500">Manage customer deployments. Each tenant gets their own subdomain.</p>
              {tenants.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-400">No tenants configured yet.</p>
              ) : (
                <div className="space-y-3">
                  {tenants.map((t: any) => (
                    <div key={t.slug} className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
                      <div>
                        <h3 className="font-medium text-gray-900">{t.name}</h3>
                        <p className="text-xs text-gray-500">{t.internalDomain}</p>
                        {t.customDomain && <p className="text-xs text-[var(--ink-0)]">{t.customDomain}</p>}
                      </div>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${t.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                        {t.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RequestCloseButton({ children, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { requestClose } = useModalClose();
  return <button type="button" {...props} onClick={requestClose}>{children}</button>;
}
